import React, { useContext, useEffect, useState } from "react";
import { HorizontalTableContext } from "@/react/features/globalization/HorizontalTable";
import { InlineRow } from "@/react/features/globalization/components/InlineRow";
import {
  BooleanCellPrintReadable,
  EditorText,
  PseudoHeader,
} from "@/react/features/globalization/cellRenderers";
import {
  booleanEdit,
  selectEdit,
  stringEdit,
  textEdit,
} from "@/react/features/globalization/components/cell-edit-renders";
import Button from "@8x8/oxygen-button";
import { ConfirmMessage } from "@/react/components/ConfirmMessage";
import useConfirmDelete from "@/react/features/globalization/hooks/useConfirmDelete";
import useHasRights from "@/react/features/globalization/hooks/useHasRights";
import styled from "styled-components";
import { COLORS } from "@/react/theme/constants";

const MainContainer = styled.div`
  padding: 16px;
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  background-color: ${COLORS.background};
  border: 1px solid rgba(0, 0, 0, 0.125);
  border-radius: 4px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 40px;
`;

export const LNPCard = ({
  data,
  isEdit,
  onUpdate,
  onDelete,
  onCreateNumberType,
}) => {
  const tableContext = useContext(HorizontalTableContext);
  let [isEditMode, setIsEditMode] = useState(false);
  const { askDelete, handleConfirmDelete, handleInfirmDelete } =
    useConfirmDelete();

  useEffect(() => {
    if (data?._created) {
      setIsEditMode(true);
    }
  }, []);

  const { lnp: canEdit } = useHasRights(tableContext.meta.rights);

  const onChangeNumberType = (row, field, selectedOption) => {
    onUpdate(row, "number_type", { id: selectedOption.value });
  };

  return (
    <MainContainer>
      <div>
        <InlineRow>
          <PseudoHeader>Number type </PseudoHeader>
          {!(isEditMode && canEdit.number_type) && data.number_type.name}
          {isEditMode &&
            canEdit.number_type &&
            selectEdit(
              tableContext.meta.numberTypesOptions,
              data,
              data.number_type.id,
              "number_type",
              onChangeNumberType,
              canEdit.number_type_create ? onCreateNumberType : undefined
            )}
        </InlineRow>
        <InlineRow>
          <PseudoHeader>Status </PseudoHeader>
          {!(isEditMode && canEdit.status) && (
            <BooleanCellPrintReadable value={data.status} />
          )}
          {isEditMode &&
            canEdit.status &&
            booleanEdit(data, "status", onUpdate)}
        </InlineRow>
        <InlineRow>
          <PseudoHeader>Lead Time </PseudoHeader>
          {!(isEditMode && canEdit.lead_time) && (
            <EditorText>{data.lead_time}</EditorText>
          )}
          {isEditMode &&
            canEdit.lead_time &&
            stringEdit(data, "lead_time", onUpdate)}
        </InlineRow>
        <InlineRow>
          <PseudoHeader>Change Lead Time </PseudoHeader>
          {!(isEditMode && canEdit.change_lead_time) && (
            <EditorText>{data.change_lead_time}</EditorText>
          )}
          {isEditMode &&
            canEdit.change_lead_time &&
            stringEdit(data, "change_lead_time", onUpdate)}
        </InlineRow>
        <InlineRow>
          <PseudoHeader>Porting Windows </PseudoHeader>
          {!(isEditMode && canEdit.porting_windows) && (
            <EditorText>{data.porting_windows}</EditorText>
          )}
          {isEditMode &&
            canEdit.porting_windows &&
            stringEdit(data, "porting_windows", onUpdate)}
        </InlineRow>
        <InlineRow>
          <PseudoHeader>Country Code</PseudoHeader>
          {!(isEditMode && canEdit.country_code) && <>{data.country_code}</>}
          {isEditMode &&
            canEdit.country_code &&
            stringEdit(data, "country_code", onUpdate)}
        </InlineRow>
        <InlineRow>
          <PseudoHeader>In Country Format</PseudoHeader>
          {!(isEditMode && canEdit.in_country_format) && (
            <EditorText>{data.in_country_format}</EditorText>
          )}
          {isEditMode &&
            canEdit.in_country_format &&
            stringEdit(data, "in_country_format", onUpdate)}
        </InlineRow>
        <InlineRow>
          <PseudoHeader>Porting Carrier</PseudoHeader>
          {!(isEditMode && canEdit.porting_carrier) && (
            <EditorText>{data.porting_carrier}</EditorText>
          )}
          {isEditMode &&
            canEdit.porting_carrier &&
            stringEdit(data, "porting_carrier", onUpdate)}
        </InlineRow>
        <InlineRow>
          <PseudoHeader>Required documents </PseudoHeader>
          {!(isEditMode && canEdit.required_documents) && (
            <EditorText>{data.required_documents}</EditorText>
          )}
          {isEditMode &&
            canEdit.required_documents &&
            textEdit(data, "required_documents", onUpdate)}
        </InlineRow>
      </div>
      {isEdit && (
        <>
          <hr />
          {askDelete && (
            <ConfirmMessage
              type={"warning"}
              handleConfirm={() => onDelete(data)}
              handleInfirm={handleInfirmDelete}
              question={"Are you sure?"}
              title={"Delete"}
            />
          )}
          <ButtonsContainer>
            {isEdit && isEditMode === false && (
              <Button
                size="small"
                variant="primary"
                onClick={() => setIsEditMode(true)}
              >
                Edit
              </Button>
            )}
            {isEditMode && (
              <Button
                size="small"
                variant="secondary"
                onClick={() => setIsEditMode(false)}
              >
                Cancel
              </Button>
            )}

            {canEdit.delete && (
              <Button
                size="small"
                variant="destructive"
                onClick={handleConfirmDelete}
              >
                Delete
              </Button>
            )}
          </ButtonsContainer>
        </>
      )}
    </MainContainer>
  );
};
